import {
  KatAlert,
  KatButton, KatDivider,
  KatLink,
  KatModal, KatSpinner, KatToggle,
} from '@amzn/katal-react';
import { Form, Formik } from 'formik';
import { useRouter } from 'next/router';
import React, { useCallback, useEffect } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from 'src/redux/hooks';
import { fetchPinInfo, selectThreadDetailOp, updatePinsForThread } from 'src/redux/slices/threadDetailSlice';
import { AppState } from 'src/redux/store';
import {
  OpPost, PinInfoResponse,
} from 'src/types/DiscussionPost';
import FetchStatus from 'src/types/FetchStatus';
import FormikField from '../FormikField';

interface PinThreadModalProps {
  threadId: string;
  onDismiss: (event: KatModal.CloseEvent | React.MouseEvent<KatButton.Element>) => void;
  onSuccess: () => void;
}

export enum PinType {
  pinnedToCategory = 'pinnedToCategory',
  pinnedToGroup = 'pinnedToGroup',
  pinnedToListing = 'pinnedToListing',
}

const {
  pinnedToCategory,
  pinnedToGroup,
  pinnedToListing,
} = PinType;

export const pinTypesToRender = {
  pinnedToCategory,
  pinnedToGroup,
  pinnedToListing,
};

const isPinnable = (pinType: PinType, pinInfo: PinInfoResponse): boolean => ({
  pinnedToCategory: pinInfo.pinnableToCategory,
  pinnedToGroup: pinInfo.pinnableToGroup,
  pinnedToListing: pinInfo.pinnableToListing,
}[pinType]!);

const getDefaultPinDescription = (pinType: PinType): string => ({
  pinnedToCategory: 'pinThread_categoryDescription',
  pinnedToGroup: 'pinThread_groupDescription',
  pinnedToListing: 'pinThread_listingDescription',
}[pinType]!);

const getAlreadyPinnedDescription = (pinType: PinType): string => ({
  pinnedToCategory: 'pinThread_categoryAlreadyPinned',
  pinnedToGroup: 'pinThread_groupAlreadyPinned',
  pinnedToListing: 'pinThread_listingAlreadyPinned',
}[pinType]!);

const getPinDescription = (pinnable: boolean, pinType: PinType): string => (
  pinnable
    ? getDefaultPinDescription(pinType)
    : getAlreadyPinnedDescription(pinType));

export enum PinThreadOptions {
}

const PinThreadModal: React.FC<PinThreadModalProps> = (
  {
    threadId,
    onDismiss,
    onSuccess,
  }: PinThreadModalProps,
) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const {
    pinInfoResponse,
    pinInfoFetchStatus: fetchStatus,
    pinUpdateStatus: updateStatus,
  } = useAppSelector(
    (state: AppState) => state.threadDetail,
  );
  const opPost = useAppSelector(selectThreadDetailOp) as OpPost;
  const categories = useAppSelector((state: AppState) => state.threadCategories.categories);
  const groups = useAppSelector((state: AppState) => state.groups.groups);

  useEffect(() => {
    dispatch(fetchPinInfo({ threadId }));
  }, []);

  const { basePath } = useRouter();

  return (
    <Formik
      initialValues={{
        pinnedToCategory: !!opPost.pinnedToCategory,
        pinnedToGroup: !!opPost.pinnedToGroup,
        pinnedToListing: !!opPost.pinnedToListing,
      }}
      onSubmit={
        async (pinnedTo) => {
          const response = dispatch(
            updatePinsForThread({
              threadId,
              ...pinnedTo,
            }),
          );
          if ((await response).meta.requestStatus === 'fulfilled') {
            onSuccess();
          }
        }
      }
    >
      {({
        setFieldValue, values, dirty,
      }) => {
        const PinTab: React.FC<{ pinType: PinType }> = useCallback(({
          pinType,
        }) => (
          <>
            <div className="flex flex-row items-center justify-between py-2">
              <div className="grid grid-cols-1 gap-1">
                <span className={`text-sm font-semibold ${(pinType === PinType.pinnedToGroup && !opPost.groupId)
                || !(fetchStatus === FetchStatus.complete && isPinnable(pinType, pinInfoResponse!))
                  ? 'opacity-50' : 'opacity-100'}`}
                >
                  {t(pinType)}
                </span>
                <div className={`${(pinType === PinType.pinnedToGroup && !opPost.groupId)
                || !(fetchStatus === FetchStatus.complete && isPinnable(pinType, pinInfoResponse!))
                  ? 'opacity-50' : 'opacity-100'}`}
                >
                  <Trans
                    i18nKey={(pinType === PinType.pinnedToGroup && !opPost.groupId)
                      ? 'pinThread_postNotInGroup'
                      : getPinDescription(
                        fetchStatus === FetchStatus.complete
                        && isPinnable(pinType, pinInfoResponse!),
                        pinType,
                      )}
                    components={{
                      categoryLink: <KatLink
                        data-testid={getPinDescription(
                          fetchStatus === FetchStatus.complete
                          && isPinnable(pinType, pinInfoResponse!),
                          pinType,
                        )}
                        href={`${basePath}/discussions?categories[]=${opPost.categoryId}`}
                        className="cursor-pointer"
                        target="_blank"
                      />,
                      groupLink: <KatLink
                        data-testid={getPinDescription(
                          fetchStatus === FetchStatus.complete
                        && isPinnable(pinType, pinInfoResponse!),
                          pinType,
                        )}
                        href={`${basePath}/groups/g/${opPost.groupId}`}
                        className="cursor-pointer"
                        target="_blank"
                      />,
                      discussionsLink: <KatLink
                        data-testid={getPinDescription(
                          fetchStatus === FetchStatus.complete
                        && isPinnable(pinType, pinInfoResponse!),
                          pinType,
                        )}
                        href={`${basePath}/discussions`}
                        className="cursor-pointer"
                        target="_blank"
                      />,
                      recentDiscussionsLink: <KatLink
                        data-testid={(fetchStatus === FetchStatus.complete
                        && isPinnable(pinType, pinInfoResponse!))
                          ? getDefaultPinDescription(pinType)
                          : getAlreadyPinnedDescription(pinType)}
                        href={`${basePath}/`}
                        className="cursor-pointer"
                        target="_blank"
                      />,
                    }}
                    values={{
                      categoryName: t(categories[opPost.categoryId].nameStringId),
                      groupName: opPost.groupId ? t(groups[opPost.groupId!].nameStringId) : '',
                    }}
                  />
                </div>
              </div>
              {fetchStatus === FetchStatus.loading
                ? <KatSpinner className="extra-small-spinner" />
                : (
                  <FormikField
                    disabled={
                      updateStatus === FetchStatus.loading
                      || fetchStatus === FetchStatus.failed
                      || (fetchStatus === FetchStatus.complete
                        && !isPinnable(pinType, pinInfoResponse!))
                      || (pinType === PinType.pinnedToGroup && !opPost.groupId)
                    }
                    name={pinType}
                    data-testid={pinType}
                    component={KatToggle}
                    textChecked={t('pinThread_toggleName')}
                    textUnchecked={t('pinThread_toggleName')}
                    checked={(values as any)[pinType]}
                    onChange={(event: KatToggle.ChangeEvent) => {
                      setFieldValue(
                        pinType,
                        (event.target as any).checked,
                      );
                    }}
                  />
                )}
            </div>
            <KatDivider variant="athens" />
          </>
        ), [values, fetchStatus, updateStatus, pinInfoResponse]);

        return (
          <Form>
            <KatModal
              data-testid="pin-thread-modal"
              onClose={(onDismiss)}
              visible
            >
              {(fetchStatus === FetchStatus.failed) && (
                <KatAlert
                  data-testid="pinthread-alert-failure"
                  className="py-[0.5rem]"
                  header={t('request_pinThread_requestFailureHeader')}
                  description={t('request_pinThread_requestFailureDescription')}
                  variant="warning"
                  persistent
                />
              )}
              {(updateStatus === FetchStatus.failed) && (
                <KatAlert
                  data-testid="pinthread-alert-failure"
                  className="py-[0.5rem]"
                  header={t('update_pinThread_requestFailureHeader')}
                  description={t('update_pinThread_requestFailureDescription')}
                  variant="warning"
                  persistent
                />
              )}
              <div slot="title">
                {t('pinThread_title')}
              </div>
              <div className="flex flex-row items-center justify-between py-2">
                <div className="mt-1 whitespace-normal text-sm font-normal">
                  {t('pinThread_titleDescription')}
                </div>
              </div>
              <KatDivider variant="athens" />
              {Object.values(pinTypesToRender).map((pinType: PinType) => (
                <PinTab
                  key={pinType}
                  pinType={pinType}
                />
              ))}
              <div slot="footer" className="kat-group-horizontal">
                <KatButton
                  data-testid="cancel-pin"
                  onClick={onDismiss}
                  variant="secondary"
                  label={t('cancel')}
                />
                <KatButton
                  data-testid="submit-pin"
                  loading={updateStatus === FetchStatus.loading}
                  disabled={fetchStatus !== FetchStatus.complete || !dirty}
                  type="submit"
                  label={t('pinThread_save')}
                />
              </div>
            </KatModal>
          </Form>
        );
      }}
    </Formik>
  );
};
export default PinThreadModal;
